import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Cookie, Shield, BarChart3, Settings2 } from "lucide-react";
import MarketingLayout from "../components/MarketingLayout";
import { Brand } from "../context/CompanyContext";

/**
 * /cookies — public Cookies Policy page.
 *
 * Plain-language explanation of which cookies we use, why, and how the
 * visitor can change preferences (the cookie banner is the action surface).
 * Stays in the Dark Luxe palette for visual consistency.
 */

const CATEGORIES = [
  {
    key: "essential",
    Icon: Shield,
    title: "Essenciais",
    badge: "Sempre activos",
    desc: "Necessários para o funcionamento do site (sessão, login, segurança CSRF e preferências de consentimento). Não podem ser desactivados — sem eles a plataforma não funciona.",
  },
  {
    key: "preferences",
    Icon: Settings2,
    title: "Preferências",
    desc: "Lembram opções como idioma, tema, modo de visualização e secções fechadas/abertas. Melhoram a experiência mas não são obrigatórios.",
  },
  {
    key: "analytics",
    Icon: BarChart3,
    title: "Análise / Estatísticas",
    desc: "Permitem-nos perceber, de forma agregada e anónima, quais páginas são mais visitadas e onde os utilizadores encontram dificuldades. Usados apenas com o seu consentimento.",
  },
];

export default function CookiesPolicy() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, []);

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden bg-[#041f1b] py-16 sm:py-24"
               data-testid="cookies-page">
        <div aria-hidden
             className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.10),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"
                data-testid="cookies-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#a0b2af] hover:text-[#d4af37] transition-colors">
            <ArrowLeft size={14} /> Voltar à Home
          </Link>

          <div className="mt-8">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              <Cookie size={14} /> Política de Cookies
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight text-[#fdfbf7]">
              Como usamos <span className="italic text-[#d4af37]">cookies</span>
            </h1>
            <p className="mt-5 text-base text-[#a0b2af] leading-relaxed">
              Esta página explica, em linguagem simples, que cookies usamos no site da
              <Brand />, com que finalidade, e como pode aceitar, rejeitar ou
              personalizar a sua escolha.
            </p>
            <p className="mt-2 text-xs text-[#a0b2af]/70">
              Última actualização: {new Date().toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}.
            </p>
          </div>

          <div className="mt-12 space-y-6">
            <Block title="O que são cookies?">
              Cookies são pequenos ficheiros de texto guardados pelo seu navegador. Permitem que
              um site reconheça o seu dispositivo entre visitas — por exemplo, para manter a sessão
              de login ou lembrar preferências. Não são programas, não acedem a outros ficheiros do
              seu dispositivo e podem ser apagados a qualquer momento nas definições do navegador.
            </Block>

            <Block title="Categorias de cookies que usamos">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {CATEGORIES.map(c => {
                  const Icon = c.Icon;
                  return (
                    <li key={c.key}
                        data-testid={`cookies-category-${c.key}`}
                        className="rounded-xl border border-[#1a4640] bg-[#062c27] p-4">
                      <div className="flex items-center gap-2.5">
                        <span className="grid place-items-center w-9 h-9 rounded-full bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30">
                          <Icon size={16} />
                        </span>
                        <h3 className="font-display text-base text-[#fdfbf7]">{c.title}</h3>
                        {c.badge && (
                          <span className="ml-auto px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30">
                            {c.badge}
                          </span>
                        )}
                      </div>
                      <p className="mt-3 text-sm text-[#a0b2af] leading-relaxed">{c.desc}</p>
                    </li>
                  );
                })}
              </ul>
            </Block>

            <Block title="Como gerir as suas preferências">
              <p>
                Da primeira vez que visitou o site apareceu um banner com 3 opções: <strong>Aceitar</strong>,
                <strong> Rejeitar</strong> e <strong>Personalizar</strong>. A sua escolha é guardada localmente
                e respeitada nas próximas visitas.
              </p>
              <p className="mt-3">
                Pode alterar a decisão a qualquer momento clicando em
                <button onClick={() => window.dispatchEvent(new CustomEvent("luxstay:open-cookie-banner"))}
                        data-testid="cookies-reopen-banner"
                        className="mx-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#041f1b] text-xs uppercase tracking-wider transition-all">
                  <Cookie size={12} /> Gerir cookies
                </button>
                . Em alternativa, pode bloquear todos os cookies nas definições do seu navegador (Chrome, Safari, Firefox, Edge) — note, no entanto, que algumas funcionalidades podem deixar de funcionar correctamente.
              </p>
            </Block>

            <Block title="Cookies de terceiros">
              <p>
                Quando aceita a categoria <em>Análise</em>, podemos usar serviços de analítica
                anonimizada (como o Google Analytics) que colocam os seus próprios cookies. Estes serviços
                têm políticas de privacidade próprias que recomendamos consultar.
              </p>
              <p className="mt-3">
                A nossa <Link to="/privacidade" className="text-[#d4af37] hover:underline">Política de Privacidade</Link> descreve em mais detalhe o tratamento de dados pessoais associado a estes cookies.
              </p>
            </Block>

            <Block title="Contacto">
              <p>
                Para qualquer dúvida sobre cookies ou privacidade, contacte-nos em
                {" "}<a href="mailto:luxstaymadeira@gmail.com" className="text-[#d4af37] hover:underline">luxstaymadeira@gmail.com</a>{" "}
                ou através do nosso{" "}
                <Link to="/contacto" className="text-[#d4af37] hover:underline">formulário de contacto</Link>.
              </p>
            </Block>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Block({ title, children }) {
  return (
    <article className="rounded-2xl border border-[#1a4640] bg-[#062c27]/50 p-6 sm:p-8">
      <h2 className="font-display text-xl sm:text-2xl text-[#fdfbf7]">{title}</h2>
      <div className="mt-3 text-sm text-[#a0b2af] leading-relaxed">{children}</div>
    </article>
  );
}
